/**
 * PaymentConfirmationModal Component
 * 
 * Modal que exibe informações de pagamento e ativa a máquininha física antes de finalizar a venda.
 * 
 * Funcionalidades:
 * - Exibe valor total da venda
 * - Exibe forma de pagamento selecionada
 * - Exibe número de autorização se fornecido
 * - Ativa máquininha física (se PagSeguro e não houver número de autorização)
 * - Aguarda confirmação do pagamento
 * - Finaliza venda após confirmação
 */

import { useState, useEffect, useCallback } from 'react'
import { CreditCard, DollarSign, Loader, CheckCircle, AlertCircle } from 'lucide-react'
import Modal from '../Modal/Modal'
import api from '../../../services/api'
import './PaymentConfirmationModal.css'

function PaymentConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  totalAmount,
  paymentData,
  saleData
}) {
  const [activating, setActivating] = useState(false)
  const [machineActivated, setMachineActivated] = useState(false)
  const [error, setError] = useState('')

  // Formatar valor monetário
  const formatCurrency = (value) => {
    const numValue = typeof value === 'number' ? value : parseFloat(value) || 0
    return numValue.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  // Verificar se é pagamento PagSeguro
  const isPagSeguroPayment = useCallback(() => {
    if (!paymentData || !Array.isArray(paymentData)) {
      console.log('🔍 [PaymentConfirmationModal] isPagSeguroPayment: paymentData inválido', { paymentData })
      return false
    }
    
    const result = paymentData.some(payment => {
      const methodName = (payment.paymentMethodName || payment.methodLabel || payment.name || '').toLowerCase()
      const isPagSeguro = methodName.includes('pagseguro') || 
             methodName.includes('pagbank') ||
             methodName.includes('tef') ||
             payment.isTefPagseguro === true ||
             payment.requiresConfirmation === true
      
      console.log('🔍 [PaymentConfirmationModal] Verificando pagamento:', {
        methodName,
        requiresConfirmation: payment.requiresConfirmation,
        isPagSeguro
      })
      
      return isPagSeguro
    })
    
    console.log('🔍 [PaymentConfirmationModal] isPagSeguroPayment resultado:', result)
    return result
  }, [paymentData])

  // Verificar se já existe número de autorização
  const hasAuthorizationNumber = useCallback(() => {
    if (!paymentData || !Array.isArray(paymentData)) {
      return false
    }
    
    const hasAuth = paymentData.some(payment => {
      const authNumber = payment.authorizationNumber || payment.authorization_number
      return authNumber && authNumber.trim().length > 0
    })
    
    console.log('🔍 [PaymentConfirmationModal] hasAuthorizationNumber:', hasAuth)
    return hasAuth
  }, [paymentData])

  // Ativar máquininha física
  const activateMachine = useCallback(async () => {
    console.log('🚀 [PaymentConfirmationModal] activateMachine chamado')
    setActivating(true)
    setError('')
    
    try {
      const rawMethod = paymentData[0]?.paymentMethodName || paymentData[0]?.methodLabel || paymentData[0]?.name || 'CREDIT_CARD'
      const methodLower = String(rawMethod).toLowerCase()
      const paymentMethodName = methodLower.includes('pix')
        ? 'PIX'
        : (methodLower.includes('débito') || methodLower.includes('debito'))
          ? 'DEBIT_CARD'
          : 'CREDIT_CARD'
      
      const requestPayload = {
        amount: totalAmount,
        paymentMethod: paymentMethodName,
        saleData: saleData
      }
      
      console.log('📤 [PaymentConfirmationModal] Enviando requisição para ativar máquininha:', requestPayload)
      
      const response = await api.post('/api/pagseguro/activate-machine', requestPayload)

      console.log('📥 [PaymentConfirmationModal] Resposta recebida:', response.data)

      if (response.data.success && response.data.activated) {
        setMachineActivated(true)
        setError('')
        console.log('✅ [PaymentConfirmationModal] Máquininha ativada com sucesso:', response.data)
      } else {
        const detail = [
          response.data.message,
          response.data.error,
          response.data.note
        ].filter(Boolean).join(' — ')
        setError(detail || 'PagBank não ativou a Moderninha (verifique token, IP whitelist e tipo de integração).')
        setMachineActivated(false)
        console.warn('⚠️  [PaymentConfirmationModal] Máquininha NÃO ativada:', response.data)
      }
    } catch (err) {
      console.error('❌ [PaymentConfirmationModal] Erro ao ativar máquininha:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      })
      setError(err.response?.data?.message || 'Erro ao ativar máquininha.')
      setMachineActivated(false)
    } finally {
      setActivating(false)
    }
  }, [paymentData, saleData, totalAmount])

  // Ativar máquininha quando modal abrir (apenas se não houver número de autorização)
  useEffect(() => {
    if (!isOpen) return

    const pagSeguro = isPagSeguroPayment()
    const hasAuth = hasAuthorizationNumber()
    
    console.log('🔍 [PaymentConfirmationModal] useEffect - Modal aberto:', {
      isOpen,
      isPagSeguro: pagSeguro,
      hasAuthorizationNumber: hasAuth,
      machineActivated,
      activating
    })
    
    // Só ativa máquina se for PagSeguro, não tiver número de autorização e ainda não foi ativada
    if (pagSeguro && !hasAuth && !machineActivated && !activating) {
      console.log('✅ [PaymentConfirmationModal] Condições atendidas, ativando máquininha...')
      activateMachine()
    } else {
      if (hasAuth) {
        console.log('⏭️  [PaymentConfirmationModal] Número de autorização já existe, pulando ativação da máquina')
      } else {
        console.log('⏭️  [PaymentConfirmationModal] Condições não atendidas, pulando ativação')
      }
    }
  }, [isOpen, isPagSeguroPayment, hasAuthorizationNumber, machineActivated, activating, activateMachine])

  // Confirmar pagamento e finalizar venda
  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm(paymentData)
    }
  }

  // Cancelar e fechar modal
  const handleCancel = () => {
    setActivating(false)
    setMachineActivated(false)
    setError('')
    if (onClose) {
      onClose()
    }
  }

  // Obter nome da forma de pagamento
  const getPaymentMethodName = () => {
    if (!paymentData || paymentData.length === 0) return 'Não informado'
    return paymentData.map(p => p.paymentMethodName || p.name || 'Não informado').join(', ')
  }

  // Verificar se há pagamento em dinheiro
  const hasCashPayment = () => {
    if (!paymentData || !Array.isArray(paymentData)) return false
    return paymentData.some(p => p.isCash === true)
  }

  // Calcular troco para pagamento em dinheiro
  const calculateCashChange = () => {
    if (!paymentData || !Array.isArray(paymentData)) return null
    
    const cashPayment = paymentData.find(p => p.isCash === true)
    if (!cashPayment) return null
    
    const cashReceived = cashPayment.cashReceived || 0
    const cashToDiscount = cashPayment.cashToDiscount || cashPayment.amount || 0
    
    if (cashReceived > cashToDiscount) {
      return cashReceived - cashToDiscount
    }
    
    return null
  }

  if (!isOpen) return null

  const pagSeguroPayment = isPagSeguroPayment()

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title="Confirmação de Pagamento"
      size="medium"
    >
      <div className="payment-confirmation-modal">
        {/* Informações da venda */}
        <div className="payment-info-section">
          <div className="payment-info-item">
            <DollarSign size={20} />
            <div className="payment-info-content">
              <span className="payment-info-label">Valor Total:</span>
              <span className="payment-info-value">R$ {formatCurrency(totalAmount)}</span>
            </div>
          </div>

          <div className="payment-info-item">
            <CreditCard size={20} />
            <div className="payment-info-content">
              <span className="payment-info-label">Forma de Pagamento:</span>
              <span className="payment-info-value">{getPaymentMethodName()}</span>
            </div>
          </div>

          {/* Exibir número de autorização se fornecido */}
          {hasAuthorizationNumber() && (
            <div className="payment-info-item">
              <CreditCard size={20} />
              <div className="payment-info-content">
                <span className="payment-info-label">Número de Autorização:</span>
                <span className="payment-info-value authorization-number">
                  {paymentData.find(p => (p.authorizationNumber || p.authorization_number))?.authorizationNumber || 
                   paymentData.find(p => (p.authorizationNumber || p.authorization_number))?.authorization_number || '-'}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Alerta de troco para pagamento em dinheiro */}
        {hasCashPayment() && calculateCashChange() !== null && calculateCashChange() > 0 && (
          <div className="cash-change-alert">
            <AlertCircle size={24} />
            <div className="cash-change-content">
              <div className="cash-change-title">⚠️ Não esqueça do troco!</div>
              <div className="cash-change-value">
                Troco: <strong>R$ {formatCurrency(calculateCashChange())}</strong>
              </div>
            </div>
          </div>
        )}

        {/* Status da máquininha (se PagSeguro e não houver número de autorização) */}
        {pagSeguroPayment && !hasAuthorizationNumber() && (
          <div className="machine-status-section">
            <div className="machine-status-header">
              <CreditCard size={20} />
              <span>Máquininha PagSeguro</span>
            </div>

            {activating && (
              <div className="machine-status activating">
                <Loader className="spinner" size={20} />
                <span>Ativando máquininha...</span>
              </div>
            )}

            {machineActivated && !activating && (
              <div className="machine-status activated">
                <CheckCircle size={20} />
                <span>Máquininha ativada. Aguardando pagamento...</span>
              </div>
            )}

            {error && (
              <div className="machine-status error">
                <AlertCircle size={20} />
                <span>{error}</span>
              </div>
            )}

            {machineActivated && (
              <div className="machine-instructions">
                <p>Por favor, realize o pagamento na máquininha.</p>
                <p>Após confirmar o pagamento, clique em "Confirmar Pagamento" abaixo.</p>
              </div>
            )}
          </div>
        )}

        {/* Mensagem para pagamentos não-PagSeguro ou quando já há número de autorização */}
        {(!pagSeguroPayment || hasAuthorizationNumber()) && (
          <div className="payment-instructions">
            <p>Confirme o recebimento do pagamento e clique em "Confirmar Pagamento" para finalizar a venda.</p>
          </div>
        )}

        {/* Botões de ação */}
        <div className="payment-confirmation-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCancel}
            disabled={activating}
          >
            Cancelar
          </button>
          
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleConfirm}
            disabled={activating || (pagSeguroPayment && !hasAuthorizationNumber() && !machineActivated)}
          >
            {activating ? (
              <>
                <Loader className="spinner" size={16} />
                Ativando...
              </>
            ) : (
              <>
                <CheckCircle size={16} />
                Confirmar Pagamento
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default PaymentConfirmationModal

